import { type MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { type UndoRedoState, useUndoRedo } from '@/hooks/use-undo-redo'
import type { UndoAction } from '@/lib/history/types/undo-action'

/**
 * Home's undo history, scoped to the spread on screen: a step recorded on another spread is skipped
 * when undoing/redoing and left out of `canUndo` / `canRedo`, so Undo and Redo only look available
 * when pressing them will change something here. Replaying it here would add or delete its elements
 * on this spread's checkpoint (a redone template drop would land on the wrong spread and be saved
 * there) and re-register the session on the one that was exited. Which steps those are changes with
 * the spread, so availability is recomputed on every spread switch. Nothing is dropped: back on that
 * spread, its steps count again.
 *
 * A step's spread is the one on screen when it was pushed, except a background step, which names
 * its own. A step that was never stamped (no spread on screen yet) is never skipped.
 *
 * `spreadIdRef` must already hold `spreadId` when this hook's effect runs (it is advanced by
 * `useHomeZoomAndSpread`, an earlier group).
 */
export function useSpreadScopedUndoRedo(
  spreadId: string,
  spreadIdRef: MutableRefObject<string>
): UndoRedoState {
  // Beside the action rather than on it, so a pushed step keeps its identity (callers compare it).
  const stepSpreadIds = useRef(new WeakMap<UndoAction, string>())

  const shouldSkip = useCallback(
    (action: UndoAction) => {
      const stepSpreadId = spreadOfStep(action, stepSpreadIds.current)

      return stepSpreadId !== undefined && stepSpreadId !== spreadIdRef.current
    },
    [spreadIdRef]
  )
  const history = useUndoRedo({ shouldSkip })
  const { refreshAvailability, pushAction: pushStep } = history

  const pushAction = useCallback(
    (action: UndoAction) => {
      if (spreadIdRef.current !== '') stepSpreadIds.current.set(action, spreadIdRef.current)
      pushStep(action)
    },
    [pushStep, spreadIdRef]
  )

  useEffect(() => {
    refreshAvailability()
  }, [spreadId, refreshAvailability])

  return { ...history, pushAction }
}

/** The spread a step was recorded on, or undefined when that isn't known. */
function spreadOfStep(
  action: UndoAction,
  stamped: WeakMap<UndoAction, string>
): string | undefined {
  if (action.type === 'spreadBackgrounds') return action.spreadId

  const own = stamped.get(action)

  if (own !== undefined || action.type !== 'compound') return own

  // A compound built while undoing (the steps of one transaction) isn't stamped itself; its steps
  // were all recorded on one spread, since a transaction is one gesture.
  return action.actions.length > 0 ? stamped.get(action.actions[0]) : undefined
}
